import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal, ClipReveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { DuotoneImage } from '@/components/site/DuotoneImage';
import { FAQList } from '@/components/site/FAQ';
import { CTABand } from '@/components/site/CTABand';
import { FaqSchema } from '@/components/site/Seo';
import { WRAP, SECTION, H2, H2_SM, Accented } from '@/components/site/ServicePageParts';
import { SITE, SERVICES, WORK } from '@/data/site';
import { CITIES } from '@/data/cities';
import { WORK_IMAGES } from '@/data/homeImages';
import { PLANS } from '@/data/pricing';
import { motionOn } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger);

// Short, city-agnostic service verbs; the city name is appended at render.
const SERVICE_ANCHORS = {
  'website-development': 'Website development',
  'web-app-development': 'Web app development',
  'mobile-app-development': 'Mobile app development',
  'custom-software-development': 'Custom software development',
  'ecommerce-website-development': 'Ecommerce website development',
  'ai-solutions': 'AI solutions & chatbots',
};

const RULE = 'border-white/[0.08]';
const LINK_CLS = 'nd-link-u text-white';
const n2 = (i) => String(i + 1).padStart(2, '0');

const ArrowDisc = () => (
  <span aria-hidden className="h-12 w-12 md:h-14 md:w-14 shrink-0 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover:bg-white group-hover:text-ink-bg group-hover:border-white">
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" className="transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:-rotate-45"><path d="M2 8h12M9 3l5 5-5 5" /></svg>
  </span>
);

function CityHero({ city }) {
  const idx = CITIES.findIndex((c) => c.slug === city.slug);
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
        style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
      />
      <div className={`relative ${WRAP} pt-16 md:pt-24 pb-16 md:pb-20`}>
        <nav aria-label="Breadcrumb" className="nd-mono flex flex-wrap items-center gap-2.5 !text-[11px]">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span aria-hidden>/</span>
          <span className="text-white">{city.name}</span>
        </nav>

        <SectionLabel as="h1" num={`${n2(idx)} / ${String(CITIES.length).padStart(2, '0')}`} data-testid={`city-${city.slug}-h1`} className="mt-12 md:mt-16">
          {city.h1}
        </SectionLabel>
        <WordReveal
          as="p"
          text={city.headline}
          accent={city.accent}
          className="mt-8 max-w-[15ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
        />

        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <Reveal delay={350} className="lg:col-span-6 lg:col-start-7 lg:row-start-1">
            <p className="text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[46ch]">{city.intro}</p>
          </Reveal>
          <Reveal delay={450} className="lg:col-span-5 lg:row-start-1">
            <div className="flex flex-wrap items-center gap-3">
              <MagneticButton href={SITE.whatsapp} external variant="primary">Book discovery call</MagneticButton>
              <MagneticButton to="/work/" variant="ghost">See our work</MagneticButton>
            </div>
          </Reveal>
        </div>
      </div>
      <CityPhoto city={city} />
    </section>
  );
}

/** Full-bleed city photograph: clip-path opens on view, image drifts on scroll. */
function CityPhoto({ city }) {
  const frame = useRef(null);
  const img = useRef(null);

  useEffect(() => {
    if (!motionOn() || !frame.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(img.current, { yPercent: -6 }, {
        yPercent: 6,
        ease: 'none',
        scrollTrigger: { trigger: frame.current, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    }, frame);
    return () => ctx.revert();
  }, []);

  return (
    <div className="relative px-4 sm:px-6 md:px-12 pb-6">
      <ClipReveal
        data-testid={`city-${city.slug}-hero-image`}
        className="relative mx-auto max-w-[1800px] aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/8] overflow-hidden rounded-[18px] ring-1 ring-white/[0.08] bg-ink-surface"
      >
        <div ref={frame} className="absolute inset-0">
          <img
            ref={img}
            src={city.image.src}
            alt={city.image.alt}
            loading="eager"
            decoding="async"
            referrerPolicy="no-referrer"
            data-testid={`city-${city.slug}-hero-image-img`}
            className="absolute -inset-y-[8%] inset-x-0 h-[116%] w-full object-cover [filter:grayscale(.9)_contrast(1.05)_brightness(.8)]"
          />
        </div>
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[linear-gradient(200deg,rgba(108,92,231,.38),transparent_55%),linear-gradient(0deg,rgba(10,10,15,.75),transparent_45%)]" />
        <div className="nd-mono pointer-events-none absolute left-5 right-5 md:left-8 md:right-8 bottom-4 md:bottom-6 flex justify-between gap-4 !text-[11px]">
          <span className="text-white">{city.name}, IN</span>
          <span className="hidden sm:inline">{city.image.alt}</span>
        </div>
      </ClipReveal>
    </div>
  );
}

const COST_TIERS = PLANS.filter((p) => ['landing-page', 'business-website', 'ecommerce', 'mobile-app'].includes(p.slug));

const STACK = ['React & Next.js', 'Shopify & headless commerce', 'Flutter & React Native', 'Node.js & Python APIs', 'PostgreSQL & MongoDB', 'OpenAI / Claude AI integrations'];

function CityStory({ city, num }) {
  const [first, ...rest] = city.story;
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num={num}>{city.name} businesses</SectionLabel>
            <Reveal>
              <h2 className={`mt-7 ${H2_SM}`}>
                Web development for how {city.name} <span className="italic text-violet">actually works.</span>
              </h2>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-7">
          <Reveal>
            <p className="font-display font-medium text-[clamp(22px,2.2vw,32px)] leading-[1.25] tracking-[-0.025em] text-white">{first}</p>
          </Reveal>
          {rest.map((para, i) => (
            <Reveal key={i} delay={(i + 1) * 80}>
              <p className="text-lg text-ink-muted leading-relaxed max-w-[62ch]">{para}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CityDesign({ city, num }) {
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8`}>
        <SectionLabel num={num} className="lg:pt-4">Design</SectionLabel>
        <div>
          <Reveal><h2 className={`${H2} max-w-[18ch]`}>{city.design.heading}</h2></Reveal>
          <Reveal delay={100}>
            <p className="mt-10 lg:ml-[33%] text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[56ch]">{city.design.body}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CityServices({ city, num }) {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
          <SectionLabel num={num} className="lg:pt-4">What we build in {city.name}</SectionLabel>
          <Reveal>
            <h2 className={`${H2} max-w-[20ch]`}>
              Six services, one team, <span className="italic text-electric">for {city.name} businesses.</span>
            </h2>
          </Reveal>
        </div>
        <ul className={`border-t ${RULE}`}>
          {SERVICES.map((s, i) => (
            <Reveal as="li" key={s.slug} delay={i * 50} className={`border-b ${RULE}`}>
              <Link
                to={`/services/${s.slug}/`}
                className="group relative isolate grid grid-cols-[1fr_auto] lg:grid-cols-[220px_1fr_auto] gap-x-8 gap-y-3 items-center py-7 md:py-9"
              >
                <span aria-hidden className="absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.14),rgba(75,159,255,.04))] transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:scale-y-100" />
                <span className="nd-mono col-span-2 lg:col-span-1">{s.num} / 0{SERVICES.length}</span>
                <h3 className="font-display font-semibold text-[clamp(26px,3.2vw,48px)] tracking-[-0.04em] leading-[1.02] transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-4">
                  {SERVICE_ANCHORS[s.slug]} in {city.name}
                </h3>
                <ArrowDisc />
                <div className="col-span-2 lg:col-start-2 lg:col-span-1">
                  <p className="text-ink-muted text-[15px] leading-relaxed max-w-[60ch]">{s.short}</p>
                  <span className="arrow-link mt-3 inline-flex items-center gap-2 nd-mono !text-[11px] group-hover:text-white transition-colors">
                    Learn more <span className="arrow">→</span>
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

function CityWhy({ city, num }) {
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num={num}>Why NioDelta in {city.name}</SectionLabel>
            <Reveal>
              <h2 className={`mt-7 ${H2_SM}`}>
                Local context, <span className="italic text-violet">studio craft.</span>
              </h2>
            </Reveal>
          </div>
        </div>
        <ol className={`lg:col-span-8 border-t ${RULE}`}>
          {city.whyLocal.map((w, i) => (
            <Reveal as="li" key={w.k} delay={i * 60} className={`border-b ${RULE}`}>
              <div className="grid grid-cols-[48px_1fr] md:grid-cols-[72px_1fr] gap-x-6 gap-y-3 py-8 md:py-10">
                <span className="nd-mono pt-2">{n2(i)}</span>
                <div>
                  <h3 className="font-display text-2xl md:text-[32px] font-semibold tracking-[-0.035em] leading-tight">{w.k}</h3>
                  <p className="mt-3 text-ink-muted leading-relaxed max-w-[58ch]">{w.v}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/** Ruled two-column index list with mono numbers. */
function RuledList({ items }) {
  return (
    <ul className={`mt-8 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-8 border-t ${RULE}`}>
      {items.map((x, i) => (
        <li key={x} className={`flex items-baseline gap-5 border-b ${RULE} py-3.5`}>
          <span className="nd-mono !text-[11px] w-7 shrink-0">{n2(i)}</span>
          <span className="text-[#C9C9D3]">{x}</span>
        </li>
      ))}
    </ul>
  );
}

function CityReach({ city, num }) {
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-2 gap-16`}>
        <div>
          <SectionLabel num={num}>Industries</SectionLabel>
          <Reveal><h2 className="mt-7 font-display font-semibold text-[clamp(28px,2.8vw,44px)] leading-[1.02] tracking-[-0.035em]">Who we build for in {city.name}</h2></Reveal>
          <RuledList items={city.industries} />
        </div>
        <div>
          <SectionLabel num={num}>Areas</SectionLabel>
          <Reveal><h2 className="mt-7 font-display font-semibold text-[clamp(28px,2.8vw,44px)] leading-[1.02] tracking-[-0.035em]">Areas we serve around {city.name}</h2></Reveal>
          <RuledList items={city.areas} />
        </div>
      </div>
    </section>
  );
}

function CityWork({ city, num }) {
  const cases = WORK.filter((w) => city.work.includes(w.slug));
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
          <SectionLabel num={num} className="lg:pt-4">Relevant work</SectionLabel>
          <Reveal><h2 className={H2}><Accented text="Projects like yours" accent="like yours" /></h2></Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-14">
          {cases.map((w, i) => (
            <Reveal key={w.slug} delay={i * 90}>
              <Link to={`/work/${w.slug}/`} className="group block">
                <ClipReveal className="relative aspect-[16/10] overflow-hidden rounded-[18px] ring-1 ring-white/[0.08] bg-ink-surface">
                  {WORK_IMAGES[w.slug] && (
                    <div className="absolute inset-0 transition-transform duration-700 [transition-timing-function:var(--ease-out)] group-hover:scale-[1.04]">
                      <DuotoneImage src={WORK_IMAGES[w.slug].src} alt={WORK_IMAGES[w.slug].alt} duotone={0.35} />
                    </div>
                  )}
                  <div aria-hidden className="absolute inset-0 bg-[linear-gradient(180deg,transparent_40%,rgba(10,10,15,.85))]" />
                  <div className="absolute bottom-5 left-6 font-display font-semibold text-[clamp(40px,4.4vw,72px)] leading-none tracking-[-0.05em] tabular-nums text-white">{w.stat.kpi}</div>
                </ClipReveal>
                <div className="mt-6 grid grid-cols-[1fr_auto] gap-x-6 items-start">
                  <div>
                    <div className="nd-mono !text-[11px]">{w.sector}</div>
                    <h3 className="mt-3 font-display text-[28px] md:text-[34px] font-semibold tracking-[-0.04em] leading-none">{w.name}</h3>
                  </div>
                  <ArrowDisc />
                  <p className="col-span-2 mt-4 text-[15px] text-ink-muted leading-relaxed max-w-[56ch]">{w.summary}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CityCost({ city, num }) {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
          <SectionLabel num={num} className="lg:pt-4">Cost &amp; timelines</SectionLabel>
          <div>
            <Reveal><h2 className={`${H2} max-w-[18ch]`}>Website development cost in {city.name}</h2></Reveal>
            <Reveal delay={100}><p className="mt-7 text-ink-muted text-[17px] leading-relaxed max-w-[60ch]">{city.costIntro}</p></Reveal>
          </div>
        </div>
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t ${RULE}`}>
          {COST_TIERS.map((t, i) => (
            <Reveal
              key={t.name}
              delay={i * 70}
              className={`border-b lg:border-b-0 ${RULE} sm:pr-6 ${i % 2 ? 'sm:border-l sm:pl-6' : ''} ${i ? 'lg:border-l lg:pl-6' : ''}`}
            >
              <div className="h-full flex flex-col pt-8 pb-10">
                <div className="flex items-center justify-between gap-4">
                  <span className="nd-mono !text-[11px]">{n2(i)}</span>
                  <span className="nd-mono !text-[11px] text-white">{t.timeline}</span>
                </div>
                <h3 className="mt-10 font-display text-xl md:text-2xl font-semibold tracking-[-0.03em]">{t.name}</h3>
                <div className="mt-5 nd-mono !text-[11px]">from</div>
                <div className="mt-1 font-display font-semibold text-[clamp(36px,3.4vw,52px)] leading-none tracking-[-0.05em] tabular-nums">{t.from}</div>
                <p className="mt-6 text-sm text-ink-muted leading-relaxed">{t.bestFor}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-sm text-ink-muted">
          Prices exclude GST.{' '}
          <Link to="/website-development-cost/" className={LINK_CLS}>
            Compare full pricing and market rates
          </Link>
          .
        </p>
        <div className={`mt-20 md:mt-28 pt-12 border-t ${RULE} grid grid-cols-1 lg:grid-cols-12 gap-10`}>
          <div className="lg:col-span-5">
            <h3 className="font-display text-[clamp(26px,2.6vw,40px)] font-semibold tracking-[-0.035em] leading-[1.05]">Technologies we use</h3>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-[48ch]">{city.stackNote}</p>
          </div>
          <div className="lg:col-span-7">
            <RuledList items={STACK} />
          </div>
        </div>
      </div>
    </section>
  );
}

function CityFaq({ city, num }) {
  const others = CITIES.filter((c) => c.slug !== city.slug);
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num={num}>FAQ</SectionLabel>
            <Reveal>
              <h2 className={`mt-7 ${H2_SM}`}>Questions from {city.name} clients</h2>
            </Reveal>
            <div className="mt-10 text-sm text-ink-muted leading-relaxed">
              Also serving{' '}
              {others.map((c, i) => (
                <React.Fragment key={c.slug}>
                  {i > 0 && (i === others.length - 1 ? ' and ' : ', ')}
                  <Link to={`${c.path}/`} className={LINK_CLS}>
                    {c.name}
                  </Link>
                </React.Fragment>
              ))}
              .
            </div>
            {city.related && (
              <ul className={`mt-6 border-t ${RULE}`}>
                {city.related.map((l) => (
                  <li key={l.to} className={`border-b ${RULE}`}>
                    <Link to={l.to} className="arrow-link group flex items-center justify-between gap-4 py-3.5 text-[15px] text-[#C9C9D3] hover:text-white transition-colors">
                      {l.label} <span className="arrow text-ink-muted group-hover:text-white">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="lg:col-span-8">
          <FAQList items={city.faqs} testIdPrefix={`city-${city.slug}-faq`} />
        </div>
      </div>
    </section>
  );
}

export default function City({ slug }) {
  const city = CITIES.find((c) => c.slug === slug);
  const hasWork = WORK.some((w) => city.work.includes(w.slug));
  const order = [
    'story',
    city.design && 'design',
    'services',
    'why',
    'reach',
    hasWork && 'work',
    'cost',
    'faq',
  ].filter(Boolean);
  const num = (k) => n2(order.indexOf(k));

  return (
    <div data-testid={`city-${slug}-page`}>
      <FaqSchema items={city.faqs} />
      <CityHero city={city} />
      <CityStory city={city} num={num('story')} />
      {city.design && <CityDesign city={city} num={num('design')} />}
      <CityServices city={city} num={num('services')} />
      <CityWhy city={city} num={num('why')} />
      <CityReach city={city} num={num('reach')} />
      {hasWork && <CityWork city={city} num={num('work')} />}
      <CityCost city={city} num={num('cost')} />
      <CityFaq city={city} num={num('faq')} />
      <CTABand
        eyebrow={`Based near ${city.name}?`}
        headline={`Let’s talk about your ${city.name} project.`}
        accent={`${city.name} project.`}
        sub="Discovery calls are 30 minutes, free, and on WhatsApp. For bigger scopes we’ll come to you."
      />
    </div>
  );
}
